/* eslint-disable no-console */

import { datadogLogs } from '@datadog/browser-logs';
import pick from 'lodash/pick';
import createLogger, { Logger } from 'pino';

import { sanitizingStringifyReplacer } from '@calm/sanitizer';

import { isServer } from './isServer';
import { JSONValue } from './types';

export { initDatadogLogs } from './datadog';

export interface logData {
	[key: string]: JSONValue | undefined;
}

export const getSanitizedLogData = (data?: logData): logData => {
	if (!data) {
		return {};
	}
	return JSON.parse(JSON.stringify(data, sanitizingStringifyReplacer()));
};

const REDACTED = '<REDACTED>';

// Bearer/Basic auth headers sometimes get logged verbatim by callers that build their own error
// message; keep the scheme word (useful for diagnosing which flow failed) and drop only the value.
// A length floor plus a lookahead requiring at least one digit/symbol in the value keeps this from
// firing on ordinary English following the word "Basic" (Calm has a "Basic" subscription tier) --
// a real token is always long and never pure letters, an English word can be either.
const BEARER_OR_BASIC_TOKEN_PATTERN =
	/\b(Bearer|Basic)\s+(?=[A-Za-z0-9\-._~+/]*[0-9\-._~+/])[A-Za-z0-9\-._~+/]{10,}=*/gi;
// Three dot-separated base64url segments is the JWT shape regardless of where it appears in a
// message (not just after "Bearer "), e.g. a session token pasted into a debug string. The
// leading boundary is captured (not asserted via \b) because \b is defined in terms of \w, which
// excludes `-` -- against a token adjacent to a hyphen that lets the match boundary retreat
// inward and leak the hyphen outside the redaction. Bounding each segment at 500 chars (rather
// than unbounded `+`) avoids the same overlapping-quantifier blowup fixed in EMAIL_PATTERN, since
// an unbounded dot-free hyphen-heavy string forces the engine to retry every split point.
const JWT_SHAPED_TOKEN_PATTERN =
	/(^|[^A-Za-z0-9_-])[A-Za-z0-9_-]{10,500}\.[A-Za-z0-9_-]{10,500}\.[A-Za-z0-9_-]{10,500}(?=[^A-Za-z0-9_-]|$)/g;
// Each domain label is matched by its own dot-free class and repeated as a `label.` group rather
// than one greedy class that also allows dots -- that overlap between the middle class and the
// trailing `\.[A-Za-z]{2,}` is what makes the engine retry every split point on a long non-email
// string, so this shape is required to avoid catastrophic backtracking. Segment lengths are
// bounded as defense in depth against any other pathological input. The leading boundary is
// captured (not asserted via \b) for the same reason as JWT_SHAPED_TOKEN_PATTERN above: \b is
// \w-relative and excludes `-`, so an email preceded by a bare hyphen would otherwise leak that
// hyphen outside the redaction.
const EMAIL_PATTERN =
	/(^|[^A-Za-z0-9])[A-Za-z0-9._%+-]{1,64}@(?:[A-Za-z0-9-]{1,63}\.){1,8}[A-Za-z]{2,63}(?=[^A-Za-z0-9]|$)/g;
// Covers both a `key=value` pair inside a URL query string and the same shape written as free
// text; the key name (and the rest of the string) survives so the message stays searchable.
// The preceding boundary is captured rather than asserted via lookbehind: this runs client-side
// through pino's browser transport and datadogLogs, and lookbehind support only reached Safari in
// 16.4 (2023) -- a lookbehind in the regex literal throws a SyntaxError at parse time on older
// Safari/iOS, which would take down the whole logger module rather than just this pattern.
// `_` is deliberately treated as a normal identifier character in that boundary (unlike a plain
// \b), so the bare alternatives (`token`, `secret`, ...) can never "peek through" an underscore --
// that gap previously both matched benign snake_case fields like `next_token=` and, for the
// camelCase spelling used throughout this codebase's own TypeScript, still failed to match
// `accessToken=`/`clientSecret=`/etc. since the preceding letter (e.g. the "s" in "accessToken")
// is alphanumeric either way. Every compound key that needs redacting is therefore enumerated
// explicitly in both spellings rather than relying on the boundary to bridge them. The value group
// excludes a trailing quote/comma/bracket (not just whitespace and `&`) so a comma-separated later
// field like `,userId=42` is left in the output rather than silently swallowed into the redacted
// value; an optional leading quote is captured separately (group 3) so a logfmt-style
// `token="secret"` still matches at all -- the previous value class excluded `"` outright, so the
// whole pattern failed to match and the quoted secret passed through completely unredacted.
// An optional non-capturing `x-` prefix wraps every alternative (still inside group 2, so the
// group numbering used by the replacement below is unaffected): `x-access-token=`, `x-api-key=`,
// `x-csrf-token=`, etc. are the dominant real-world spelling for custom credential headers and
// were missing entirely from the enumerated list.
// `-` is excluded from the boundary for the same reason as `_` above: without that, the bare
// alternatives could bridge a kebab-case field like `next-token=` (a benign pagination cursor)
// the same way they previously bridged `next_token=` -- so kebab-case spellings of every compound
// key are enumerated explicitly alongside the camelCase/snake_case ones rather than relying on the
// boundary to cover them.
const CREDENTIAL_KEY_VALUE_PATTERN =
	/(^|[^A-Za-z0-9_-])((?:x-)?(?:sessionToken|session_token|session-token|session|accessToken|access_token|access-token|authToken|auth_token|auth-token|apiToken|api_token|api-token|refreshToken|refresh_token|refresh-token|idToken|id_token|id-token|puidToken|puid_token|puid-token|clientSecret|client_secret|client-secret|userPassword|user_password|user-password|csrfToken|csrf_token|csrf-token|token|apiKey|api_key|api-key|apikey|password|secret))=(["']?)([^\s&"',;)\]}]+)/gi;

/**
 * Redact credential-shaped substrings from a log message before it reaches pino or Datadog.
 *
 * Unlike `getSanitizedLogData`/`getSanitizedError`, this never consults `SECURE_KEY_LIST` or any
 * object-key-based rule: `msg` is a bare string, so a key-match sanitizer can only ever no-op on
 * it, and the list's substring-match fallback path would nuke any message merely containing "p"
 * or "token" as an English word. Only the specific patterns below are redacted.
 */
export const getSanitizedMessage = (msg: string): string => {
	if (!msg) {
		return msg;
	}
	return (
		msg
			// EMAIL_PATTERN must run first, before BEARER_OR_BASIC_TOKEN_PATTERN and
			// JWT_SHAPED_TOKEN_PATTERN: an email's local part right after "Bearer "/"Basic " would
			// otherwise get eaten by the token value class (which stops at `@` but still consumes
			// everything before it), stranding the domain in plaintext right after the redaction; the
			// same greedy-consumption risk applies to a domain with 3+ long labels against the JWT
			// pattern's "three segments" shape starting at the `@`.
			.replace(EMAIL_PATTERN, (_match, boundary: string) => `${boundary}${REDACTED}`)
			.replace(BEARER_OR_BASIC_TOKEN_PATTERN, `$1 ${REDACTED}`)
			.replace(JWT_SHAPED_TOKEN_PATTERN, (_match, boundary: string) => `${boundary}${REDACTED}`)
			.replace(CREDENTIAL_KEY_VALUE_PATTERN, `$1$2=$3${REDACTED}`)
	);
};

const networkShape = {
	status: true,
	statusText: true,
	headers: true,
	config: ['method', 'url', 'params', 'headers', 'data'],
	request: ['res.headers'],
	data: true,
} as const;
const networkKeys = Object.keys(networkShape);

/**
 * Redact credential-bearing fields from a value that is about to be relayed to a client.
 *
 * A proxy route that forwards an upstream error body must not forward a token or an email
 * address just because the upstream chose to include one. Callers pass the body explicitly
 * rather than relying on any other function to have scrubbed it in passing.
 */
export const getSanitizedResponseBody = (body: unknown): unknown => {
	try {
		return JSON.parse(JSON.stringify(body, sanitizingStringifyReplacer()));
	} catch {
		// Never forward an unsanitized body: drop it rather than risk relaying a credential.
		return undefined;
	}
};

// A property can be a getter that throws, and every read here happens while the caller is
// already inside a catch block -- an exception would escalate a logged error into a failed
// request, so no single hostile property may abort the pass.
const safeGet = (error: object, key: string): unknown => {
	try {
		return (error as Record<string, unknown>)[key];
	} catch {
		return undefined;
	}
};

const isNetworkError = (error: object): boolean => {
	try {
		const errorKeys = Object.keys(error);
		return errorKeys.includes('status') && errorKeys.includes('statusText');
	} catch {
		return false;
	}
};

// Network errors (axios responses) carry large vendor objects (config, request) with mostly
// irrelevant nesting, so only networkShape's keys survive, picking sub-paths where specified.
const pruneToNetworkShape = (error: object): Record<string, unknown> => {
	const pruned: Record<string, unknown> = {};
	networkKeys.forEach(key => {
		if (!(key in error)) {
			return;
		}
		let value = safeGet(error, key) as Record<string, unknown> | undefined;
		const keysToPick = networkShape[key as keyof typeof networkShape];
		if (Array.isArray(keysToPick)) {
			value = pick(value, keysToPick);
		}
		if (key === 'config' && typeof value?.data === 'string') {
			try {
				value = { ...value, data: JSON.parse(value.data) };
			} catch {
				// Outbound request bodies aren't always JSON; keep the raw string if it doesn't parse.
			}
		}
		pruned[key] = value;
	});
	return pruned;
};

// message and stack are non-enumerable on real Error instances (spreading an Error drops them),
// and code/type are diagnostic identifiers rather than values that could carry credentials, so
// none of the four go through the sanitizer -- they're reattached verbatim below instead.
const passthroughKeys = ['message', 'stack', 'code', 'type'] as const;

export const getSanitizedError = (error: unknown): unknown => {
	// Callers hand over whatever `catch` gave them, and `throw 'string'` is legal JS, so a
	// primitive can reach here despite the type. Property access below would throw on one,
	// and this runs inside the caller's catch block -- crashing here would escalate a logged
	// error into a failed request. Pass primitives through untouched instead.
	if (!error || typeof error !== 'object') {
		return error;
	}
	const message = safeGet(error, 'message');
	const stack = safeGet(error, 'stack');
	// Rebuild on the original prototype so the emitted `type` still names the real class --
	// TypeError, AggregateError, or a plain object's Object -- instead of flattening to Error.
	//
	// Host classes are the exception: DOMException (an AbortError from a cancelled fetch is one)
	// exposes inherited accessors backed by internal slots, which Object.create cannot reproduce,
	// so reading `name` on such an object throws. Serializers do read it, so probe here and fall
	// back to a plain Error rather than hand back an object that explodes downstream.
	const buildResult = (): Error => {
		try {
			const candidate = Object.create(Object.getPrototypeOf(error)) as Error;
			const { name } = candidate;
			return name === undefined || typeof name === 'string' ? candidate : new Error();
		} catch {
			return new Error();
		}
	};
	// defineProperty rather than assignment throughout: these keys are attacker-influenced, and a
	// plain write to `__proto__` invokes the inherited setter and swaps the object's prototype.
	const define = (target: object, key: string, value: unknown): void => {
		Object.defineProperty(target, key, { value, enumerable: true, writable: true, configurable: true });
	};

	// Shaping and rebuilding are guarded as one unit: enumerating own keys and reading any
	// property can throw (a throwing getter, a hostile Proxy), and an exception escaping here
	// would surface as a failed request rather than a log line.
	try {
		const shaped: Record<string, unknown> = isNetworkError(error) ? pruneToNetworkShape(error) : { ...error };
		passthroughKeys.forEach(key => delete shaped[key]);
		const sanitized: Record<string, unknown> = JSON.parse(
			JSON.stringify(shaped, sanitizingStringifyReplacer()),
		);
		const result = buildResult();
		define(result, 'message', message);
		define(result, 'stack', stack);
		Object.entries(sanitized).forEach(([key, value]) => define(result, key, value));
		// Diagnostic identifiers, reattached verbatim because they never went through the sanitizer.
		(['code', 'type'] as const).forEach(key => {
			if (key in error) {
				define(result, key, safeGet(error, key));
			}
		});
		return result;
	} catch {
		// Fail closed: keep only the two fields read defensively above, never fall back to the
		// unsanitized error.
		const result = buildResult();
		define(result, 'message', message);
		define(result, 'stack', stack);
		return result;
	}
};

const formatForBrowser =
	(logFn: typeof console.log): ((o: object) => void) =>
	(o: object): void => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { level, time, err, msg, ...data } = o as {
			level: string | number;
			time: number;
			err?: Error;
			msg: string;
		};
		logFn(...[msg, data, err].filter(Boolean));
	};

export class CalmLogger {
	private logger: Logger;
	private shouldSkipConsoleLog: boolean;

	public constructor(
		childOptions: Parameters<ReturnType<typeof createLogger>['child']>[0] = {},
		shouldSkipConsoleLog?: boolean,
	) {
		const pino = createLogger({
			// pino represents log levels as numbers, while
			// Datadog expects them as strings. This formatter
			// ensures that the log level is sent as a string.
			formatters: {
				level(level) {
					return { level };
				},
			},
			browser: {
				write: {
					fatal: formatForBrowser(console.error),
					error: formatForBrowser(console.error),
					warn: formatForBrowser(console.warn),
					info: formatForBrowser(console.info),
					debug: formatForBrowser(console.debug),
					trace: formatForBrowser(console.debug),
				},
			},
			errorKey: 'error',
		});
		this.logger = pino.child(childOptions);
		this.shouldSkipConsoleLog = shouldSkipConsoleLog ?? false;
	}

	public info(msg: string, data?: logData): void {
		const cleanMsg = getSanitizedMessage(msg);
		const cleanData = getSanitizedLogData(data);
		if (!this.shouldSkipConsoleLog || isServer()) {
			this.logger.info(cleanData, cleanMsg);
		}
		if (!isServer()) {
			datadogLogs.logger.info(cleanMsg, cleanData);
		}
	}

	public warn(msg: string, data?: logData): void {
		const cleanMsg = getSanitizedMessage(msg);
		const cleanData = getSanitizedLogData(data);
		if (!this.shouldSkipConsoleLog || isServer()) {
			this.logger.warn(cleanData, cleanMsg);
		}
		if (!isServer()) {
			datadogLogs.logger.warn(cleanMsg, cleanData);
		}
	}

	public debug(msg: string, data?: logData): void {
		const cleanMsg = getSanitizedMessage(msg);
		const cleanData = getSanitizedLogData(data);
		if (!this.shouldSkipConsoleLog || isServer()) {
			this.logger.debug(cleanData, cleanMsg);
		}
		if (!isServer()) {
			// eslint-disable-next-line testing-library/no-debugging-utils
			datadogLogs.logger.debug(cleanMsg, cleanData);
		}
	}

	public fatal(msg: string, data?: logData): void {
		const cleanMsg = getSanitizedMessage(msg);
		const cleanData = getSanitizedLogData(data);
		this.logger.fatal(cleanData, cleanMsg);
	}

	public error(msg: string, data: logData, err: unknown): void {
		const cleanMsg = getSanitizedMessage(msg);
		const cleanData = getSanitizedLogData(data);
		const cleanError = getSanitizedError(err);
		if (!this.shouldSkipConsoleLog || isServer()) {
			this.logger.error({ ...cleanData, error: cleanError }, cleanMsg);
		}
		if (!isServer()) {
			// The SDK requires an Error here; a non-Error only reaches this point when a caller threw
			// a primitive, which the sanitizer returns untouched.
			datadogLogs.logger.error(cleanMsg, cleanData, cleanError as Error | undefined);
		}
	}
}
